'use client';
import { useEffect } from 'react';
import posthog from 'posthog-js';
import { POSTHOG_CLIENT_READY_EVENT } from '../../analytics/posthog-client-readiness';

/** Allowlisted authenticated person properties; email is identity PII and never an event property. */
export interface PostHogIdentityProperties {
  userType?: 'creator' | 'fan' | 'moderator';
  email?: string;
}

/** Authenticated PostHog identity with the approved searchable email person property. */
export interface PostHogUserIdentity {
  distinctId?: string;
  properties?: PostHogIdentityProperties;
}

/** Identifies an authenticated web user unless they explicitly opted out of analytics capture. */
export function usePostHogIdentity(userIdentity?: PostHogUserIdentity) {
  const distinctId = userIdentity?.distinctId;
  const userType = userIdentity?.properties?.userType;
  const email = userIdentity?.properties?.email?.trim();

  useEffect(() => {
    if (!distinctId) return;

    const identifyAuthenticatedUser = () => {
      if (posthog.has_opted_out_capturing()) return;
      const properties =
        userType || email
          ? {
              ...(userType ? { userType } : {}),
              ...(email ? { email } : {}),
            }
          : undefined;
      if (posthog.get_distinct_id() !== distinctId) {
        posthog.identify(distinctId, properties);
      } else if (properties) {
        posthog.setPersonProperties(properties);
      }
    };

    identifyAuthenticatedUser();
    window.addEventListener(POSTHOG_CLIENT_READY_EVENT, identifyAuthenticatedUser);
    const unsubscribe = posthog.on('eventCaptured', (event) => {
      if (event?.event === '$opt_in') identifyAuthenticatedUser();
    });

    return () => {
      window.removeEventListener(POSTHOG_CLIENT_READY_EVENT, identifyAuthenticatedUser);
      unsubscribe();
    };
  }, [distinctId, email, userType]);
}
